import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { auth, sendEmailVerification, checkEmailVerified } from '../../firebase';
import { toast } from 'react-hot-toast';

const OnboardingStepVerifyEmail = ({ onVerified }) => {
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const intervalRef = useRef(null);

  const email = auth.currentUser?.email;

  // Poll for verification every 3 seconds
  useEffect(() => {
    const poll = async () => {
      if (!auth.currentUser) return;
      const verified = await checkEmailVerified(auth.currentUser);
      if (verified) {
        onVerified();
      }
    };

    intervalRef.current = setInterval(poll, 3000);
    poll();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [onVerified]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;

    setResending(true);
    try {
      await sendEmailVerification(auth.currentUser);
      toast.success('Verification email sent');
      setCooldown(60);
    } catch (err) {
      toast.error('Could not send email. Try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.8 }}
      >
        <p className="text-[13px] tracking-[0.2em] uppercase text-[#f97316] mb-6 font-medium">
          Verify your email
        </p>

        {/* Animated envelope icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5, type: 'spring', stiffness: 200 }}
          className="relative w-20 h-20 mx-auto mb-8"
        >
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#f97316]/10 to-[#ea580c]/5" />
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-9 h-9 text-[#f97316]" fill="none" viewBox="0 0 24 24" strokeWidth={1.2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          {/* Subtle pulse ring */}
          <motion.div
            className="absolute inset-0 rounded-2xl border border-[#f97316]/20"
            animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0, 0.4] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        <h1 className="text-[2rem] sm:text-[2.5rem] font-light tracking-tight leading-[1.1] text-gray-900 mb-3">
          Check your <span className="text-[#f97316]">inbox</span>
        </h1>

        <p className="text-[15px] text-gray-400 leading-relaxed max-w-sm mx-auto mb-2">
          We sent a verification link to
        </p>

        {email && (
          <p className="text-[15px] text-gray-900 font-medium mb-8">
            {email}
          </p>
        )}

        {/* Status card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="bg-white border border-gray-100 rounded-xl p-5 mb-8 max-w-sm mx-auto"
          style={{
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <motion.div
              className="flex gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-[#f97316]"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
                />
              ))}
            </motion.div>
            <span className="text-[13px] text-gray-500">Waiting for confirmation</span>
          </div>

          <div className="space-y-2.5">
            {[
              { label: 'Open the email from Klorah', done: false },
              { label: 'Click the verification link', done: false },
              { label: 'Come back here — we\'ll detect it automatically', done: false },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-2.5 text-left">
                <div className="w-5 h-5 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] text-gray-400 font-medium">{idx + 1}</span>
                </div>
                <span className="text-[13px] text-gray-500 leading-snug">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="space-y-4"
      >
        <button
          onClick={handleResend}
          disabled={cooldown > 0 || resending}
          className="w-full py-4 bg-[#f97316] hover:bg-[#ea580c] text-white text-[15px] font-medium rounded-xl transition-colors disabled:opacity-40 disabled:hover:bg-[#f97316]"
        >
          {resending
            ? 'Sending...'
            : cooldown > 0
              ? `Resend in ${cooldown}s`
              : 'Resend verification email'}
        </button>

        <p className="text-[12px] text-gray-400">
          Check your spam folder if you don't see it
        </p>
      </motion.div>
    </div>
  );
};

export default OnboardingStepVerifyEmail;
